/**
 * The lobby is the same table before anyone sits: felt, a pad of past matches,
 * and the two ways to start. The live table is the one thing that gets brass.
 */
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import type { GameId } from '@cards/shared'
import { GAME_TABS, getGameUi } from '../games/registry'
import { signed } from '../table/atoms'
import { SuitText } from '../table/atoms'
import { matchesPath, resultClass, resultWord, toMatchRows, type MatchRow } from '../history/matches'
import { api, getAccount } from '../auth/session'
import { buzz } from '../settings/haptics'
import { HeroFan } from './HeroFan'
import { introOf } from './gameIntro'
import type { MatchesResponse, OpenTablesResponse, TablePreview } from '@cards/shared'

/**
 * The player's recent matches in the game whose tab is selected, read when
 * this screen appears and again whenever the tab changes: each game keeps its
 * own record.
 *
 * It used to be fetched once by `App`, on load. That is the one moment the
 * list is guaranteed to be stale for the case that matters: a player who has
 * just finished a match arrives here and it is not listed, so there is no
 * "All" link either, so the whole record — including the replay — is
 * unreachable until they reload the page.
 *
 * Fetching it where it is shown also avoids racing the server's write.
 * Re-reading on the match's *finish* would have been the obvious fix and is
 * wrong: the row is written asynchronously at that moment, so the read
 * overtakes it. By the time somebody has left the table and landed here, it
 * exists.
 */
function useRecentMatches(game: GameId): readonly MatchRow[] {
  const [recent, setRecent] = useState<readonly MatchRow[]>([])
  useEffect(() => {
    let live = true
    const me = getAccount()?.displayName ?? ''
    // Clear first: the rows on screen belong to the tab just left.
    setRecent([])
    void api<MatchesResponse>(matchesPath(game))
      .then((r) => { if (live) setRecent(toMatchRows(r.matches ?? [], me)) })
      .catch(() => { if (live) setRecent([]) })
    return () => { live = false }
  }, [game])
  return recent
}

/** How often the open tables are read again while this screen is up. A seat
 *  goes quickly, and a listing that is a minute old offers seats that are gone. */
const OPEN_POLL_MS = 10_000
/** The most open tables the screen lists; the actions below must stay on it. */
const OPEN_MAX = 3

/**
 * The tables whose hosts opened them to anyone and that still have a seat
 * free, in every game — read on arrival and every few seconds after, but only
 * while the page is visible.
 *
 * Every game, not the selected tab's: the tab is 500 on every load, so asking
 * for one game hid a table opened to anyone at Belote, Pitch or Bridge from
 * everybody who had not happened to switch to that tab first. The selected
 * game's tables are listed first (see `byGame`).
 */
function useOpenTables(): readonly TablePreview[] {
  const [open, setOpen] = useState<readonly TablePreview[]>([])
  useEffect(() => {
    let live = true
    const read = (): void => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      void api<OpenTablesResponse>('/api/tables/open')
        .then((r) => { if (live) setOpen(r.tables ?? []) })
        .catch(() => { if (live) setOpen([]) })
    }
    read()
    const timer = window.setInterval(read, OPEN_POLL_MS)
    document.addEventListener('visibilitychange', read)
    return () => {
      live = false
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', read)
    }
  }, [])
  return open
}

/** The selected game's tables first, then the rest; newest first within each,
 *  as the server sends them (the sort is stable). */
export function byGame(tables: readonly TablePreview[], game: GameId): TablePreview[] {
  return [...tables].sort((a, b) => Number(b.gameId === game) - Number(a.gameId === game))
}

/** Every table code is this long; the server refuses anything else. */
const CODE_LENGTH = 6
/** A tab change re-deals the hero fan; this is the tick under the thumb. */
const BUZZ_TAB = 6

/** The most rows the lobby's pad will ever show; the rest are behind "See all". */
const RECENT_MAX = 5
/** `.pad { --pad-rule }`: every row on the pad is this tall. */
const PAD_RULE = 28
/** The pad's own top and bottom padding, set inline below. */
const PAD_PAD = 10

/**
 * How many whole rows of the recent pad fit in the space the page leaves it.
 *
 * The section is given whatever height is left between the tabs (or the live
 * table) and the actions at the bottom, and no more: listing every match the
 * server sends (up to fifty) grew the page past the fold and pushed "New
 * table" off the screen. Counting rows rather than clipping the pad keeps it a
 * pad — no half line cut off at the bottom edge.
 */
function useFittingRows(present: boolean): [RefObject<HTMLElement>, RefObject<HTMLDivElement>, number] {
  const box = useRef<HTMLElement>(null)
  const head = useRef<HTMLDivElement>(null)
  const [rows, setRows] = useState(RECENT_MAX)
  useLayoutEffect(() => {
    const el = box.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const measure = (): void => {
      const gap = parseFloat(getComputedStyle(el).rowGap) || 0
      const room = el.clientHeight - (head.current?.offsetHeight ?? 0) - gap - PAD_PAD * 2
      setRows(Math.max(0, Math.min(RECENT_MAX, Math.floor(room / PAD_RULE))))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
    // The section only exists once there are matches to list, and in the live
    // app they arrive after the first render, so it is measured when it
    // appears rather than once on mount, when there was nothing to measure.
  }, [present])
  return [box, head, rows]
}

export interface LiveTable {
  readonly tableId: string
  /** Its join code, so the open tables do not offer the player their own. */
  readonly code: string
  /** One sentence: `Hand 4, trick 3. Dev holds 8♥ and it is your turn.` */
  readonly sentence: string
  readonly heading: string
}

export function Home({
  game,
  onGame,
  live,
  onResume,
  onJoin,
  onNewTable,
  onSettings,
  onAllMatches,
  recent: given,
  open: givenOpen,
}: {
  game: GameId
  onGame: (id: GameId) => void
  live: LiveTable | null
  onResume: () => void
  onJoin: (code: string) => void
  onNewTable: () => void
  onSettings: () => void
  onAllMatches: () => void
  /** Fixture override for the demo harness; the live app leaves it out and the
   *  screen reads its own. */
  recent?: readonly MatchRow[]
  /** Fixture override for the demo harness, as `recent` is. */
  open?: readonly TablePreview[]
}): JSX.Element {
  const [code, setCode] = useState('')
  const fetched = useRecentMatches(game)
  // The demo harness has no server, and its whole job is to screenshot this
  // screen with real rows in it, so it supplies them.
  const recent = given ?? fetched
  const [recentBox, recentHead, fits] = useFittingRows(recent.length > 0)
  const shown = recent.slice(0, fits)
  const fetchedOpen = useOpenTables()
  const intro = introOf(game)
  const canJoin = code.length === CODE_LENGTH
  const open = byGame(givenOpen ?? fetchedOpen, game).filter((t) => t.code !== live?.code).slice(0, OPEN_MAX)

  return (
    <div className="page">
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="wordmark">
          cards{' '}
          <span className="suit">
            <span className="s-s">♠</span>
            <span className="s-h">♥</span>
            <span className="s-d">♦</span>
            <span className="s-c">♣</span>
          </span>
        </div>
        <button type="button" className="head-btn" onClick={onSettings}>
          Settings
        </button>
      </header>

      <nav className="games" aria-label="Game">
        {GAME_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={tab.id === game ? 'on' : undefined}
            aria-pressed={tab.id === game}
            disabled={tab.soon}
            onClick={() => {
              if (tab.id === game) return
              buzz(BUZZ_TAB)
              onGame(tab.id)
            }}
          >
            {tab.name}
            {tab.soon ? <small>soon</small> : null}
          </button>
        ))}
      </nav>

      {live ? (
        <section className="live">
          <h2>{live.heading}</h2>
          <p>
            <SuitText text={live.sentence} />
          </p>
          <button className="btn btn-primary btn-block" onClick={onResume}>
            Resume
          </button>
        </section>
      ) : (
        // No table waiting, so the screen opens on the game itself: a hand of
        // its own cards and one sentence on how it is played.
        // A returning player's record and the open tables need the room, so
        // with either on screen the fan sits small beside the words.
        <section className={`hero${recent.length > 0 || open.length > 0 ? ' compact' : ''}`} aria-labelledby="hero-name">
          <HeroFan game={game} />
          <div className="hero-words">
            <h1 id="hero-name">{intro.name}</h1>
            <p>{intro.line}</p>
          </div>
        </section>
      )}

      {open.length > 0 ? (
        <section style={{ flex: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="section-head">Open tables</div>
          <ul className="open-tables">
            {open.map((t) => (
              <OpenTableLine key={t.code} table={t} otherGame={t.gameId !== game} onJoin={() => onJoin(t.code)} />
            ))}
          </ul>
        </section>
      ) : null}

      {recent.length > 0 ? (
        // Takes the room left over and no more (see useFittingRows), so the
        // actions below always stay on screen.
        <section
          ref={recentBox}
          style={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          {/* With no room for a single row the heading would sit over nothing,
              so it is hidden; hidden, not removed, so the measurement that
              decides how many rows fit does not change under it. */}
          <div
            className="section-head"
            ref={recentHead}
            style={shown.length === 0 ? { visibility: 'hidden' } : undefined}
            aria-hidden={shown.length === 0 || undefined}
          >
            Recent matches
            <button type="button" className="head-btn" onClick={onAllMatches}>
              See all
            </button>
          </div>
          {shown.length > 0 ? (
            <div className="pad cols" style={{ flex: 'none', paddingTop: PAD_PAD, paddingBottom: PAD_PAD }}>
              {shown.map((m) => (
                <MatchLine key={m.id} match={m} />
              ))}
            </div>
          ) : null}
        </section>
      ) : null}

      <div style={{ flex: 'none', marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <form
          className="join"
          onSubmit={(e) => {
            e.preventDefault()
            if (canJoin) onJoin(code)
          }}
        >
          <label className="code-slots">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, CODE_LENGTH))}
              aria-label="Table code"
              autoCapitalize="characters"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="go"
              maxLength={CODE_LENGTH}
            />
            <span className="slots" aria-hidden="true">
              {Array.from({ length: CODE_LENGTH }, (_, i) => (
                <span key={i} className={`slot${i === code.length ? ' next' : ''}${code[i] ? ' filled' : ''}`}>
                  {code[i] ?? ''}
                </span>
              ))}
            </span>
            <span className="code-caption">Have a code? Type it here.</span>
          </label>
          <button type="submit" className="btn btn-secondary" disabled={!canJoin}>
            Join
          </button>
        </form>
        {/* Brass belongs to the one thing to do next. With a table waiting
            that is Resume, so a new table steps back to the secondary rank. */}
        <button
          type="button"
          className={`btn ${live ? 'btn-secondary' : 'btn-primary'} btn-block`}
          style={{ height: 50 }}
          onClick={onNewTable}
        >
          New table
        </button>
      </div>
    </div>
  )
}

function MatchLine({ match }: { match: MatchRow }): JSX.Element {
  return (
    <>
      <div className="k">
        <span className={resultClass(match.won)}>{resultWord(match.won)}</span>
        <span className="who">{match.note}</span>
      </div>
      <div className={`r${match.us < 0 ? ' minus' : ''}`}>{signed(match.us)}</div>
      <div
        className={`r${match.them < 0 ? ' minus' : ''}`}
        style={match.them < 0 ? undefined : { color: 'var(--record-text-2)' }}
      >
        {signed(match.them)}
      </div>
    </>
  )
}

function OpenTableLine({
  table,
  otherGame,
  onJoin,
}: {
  table: TablePreview
  /** Not the selected tab's game, so the line says which game it is. */
  otherGame: boolean
  onJoin: () => void
}): JSX.Element {
  const free = Math.max(0, table.seatCount - table.seatsFilled)
  return (
    <li className="open-table">
      <div className="open-table-text">
        <b>{table.hostName ? `${table.hostName}'s table` : `Table ${table.code}`}</b>
        <span>
          {otherGame ? `${getGameUi(table.gameId).name}, ` : ''}
          {free === 1 ? '1 seat free' : `${free} seats free`}, table {table.code}
        </span>
      </div>
      <button type="button" className="btn btn-secondary" onClick={onJoin}>
        Join
      </button>
    </li>
  )
}
